/* Stroomkaart - i18n engine
 * ============================================
 * Lightweight runtime translation system. Ports designer's assets/js/i18n.js
 * to an ES module + reads supported languages + default from envConfig.brand
 * so each customer carries its own language config.
 *
 * Page templates declare strings via:
 *   data-i18n="key.path"            replaces textContent
 *   data-i18n-html="key.path"       replaces innerHTML (rich content with <strong>, <a>)
 *   data-i18n-attr="placeholder:key.path,title:other.key"   replaces attributes
 * Page <html data-i18n-title="key"> sets document.title.
 */

import { envConfig } from '../../envConfig';
import { translations } from './translations';

const STORAGE_KEY = 'sk_lang_v1';

let currentLang: string = '';

function supported(): readonly string[] {
  return envConfig.brand.languages;
}

function defaultLang(): string {
  return envConfig.brand.defaultLanguage;
}

function detectLanguage(): string {
  const allowed = supported();

  // 1. URL ?lang=
  try {
    const url = new URL(window.location.href);
    const fromUrl = url.searchParams.get('lang');
    if (fromUrl && allowed.includes(fromUrl)) {
      try { localStorage.setItem(STORAGE_KEY, fromUrl); } catch { /* private mode */ }
      return fromUrl;
    }
  } catch { /* invalid URL */ }

  // 2. localStorage
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && allowed.includes(stored)) return stored;
  } catch { /* private mode */ }

  // 3. <html lang>
  const htmlLang = (document.documentElement.lang || '').toLowerCase().slice(0, 2);
  if (allowed.includes(htmlLang)) return htmlLang;

  // 4. Brand default
  return defaultLang();
}

function getByPath(obj: unknown, path: string): unknown {
  if (!obj || !path) return undefined;
  const parts = path.split('.');
  let cur: unknown = obj;
  for (const part of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[part];
  }
  return cur;
}

function dictFor(lang: string): unknown {
  return (translations as Record<string, unknown>)[lang] ?? {};
}

function translate(key: string, lang: string): string | null {
  const v = getByPath(dictFor(lang), key);
  if (typeof v === 'string') return v;
  const fb = getByPath(dictFor(defaultLang()), key);
  if (typeof fb === 'string') return fb;
  // Visible marker only when the global debug flag is set.
  if ((window as unknown as { SK_I18N_DEBUG?: boolean }).SK_I18N_DEBUG) return `⟦${key}⟧`;
  return null;
}

function applyToRoot(root: ParentNode, lang: string): void {
  root.querySelectorAll<HTMLElement>('[data-i18n]').forEach((el) => {
    const key = el.getAttribute('data-i18n');
    if (!key) return;
    const t = translate(key, lang);
    if (t !== null) el.textContent = t;
  });

  root.querySelectorAll<HTMLElement>('[data-i18n-html]').forEach((el) => {
    const key = el.getAttribute('data-i18n-html');
    if (!key) return;
    const t = translate(key, lang);
    if (t !== null) el.innerHTML = t;
  });

  root.querySelectorAll<HTMLElement>('[data-i18n-attr]').forEach((el) => {
    const spec = el.getAttribute('data-i18n-attr');
    if (!spec) return;
    spec.split(',').forEach((pair) => {
      const [attr, key] = pair.split(':').map((s) => s.trim());
      if (!attr || !key) return;
      const t = translate(key, lang);
      if (t !== null) el.setAttribute(attr, t);
    });
  });
}

function applyDocumentLanguage(lang: string): void {
  document.documentElement.setAttribute('lang', lang);
  const titleKey = document.documentElement.getAttribute('data-i18n-title');
  if (titleKey) {
    const t = getByPath(dictFor(lang), titleKey);
    if (typeof t === 'string') document.title = t;
  }
}

function updateSwitcherUI(lang: string): void {
  document.querySelectorAll<HTMLElement>('[data-lang-switcher]').forEach((sw) => {
    sw.querySelectorAll<HTMLElement>('[data-lang]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.getAttribute('data-lang') === lang);
    });
    // Hide the switcher entirely on single-language brands (e.g. Bristol).
    sw.hidden = supported().length < 2;
  });
}

/**
 * Wires click handlers onto every <c>[data-lang-switcher]</c> currently in
 * the DOM and refreshes their active-button highlight. Exported so callers
 * that inject lang switchers after initial page load (e.g. the admin top
 * bar built by shell.ts) can rebind them. Idempotent only insofar as the
 * caller doesn't re-attach to elements already wired - the listener is
 * <em>added</em> each call. New switchers each page-render are fine.
 */
export function bindLangSwitchers(): void {
  document.querySelectorAll<HTMLElement>('[data-lang-switcher]').forEach((sw) => {
    sw.addEventListener('click', (e) => {
      const target = e.target as HTMLElement | null;
      const btn = target?.closest<HTMLElement>('[data-lang]');
      if (!btn) return;
      e.preventDefault();
      const lang = btn.getAttribute('data-lang');
      if (lang) setLanguage(lang);
    });
  });
  updateSwitcherUI(getLanguage());
}

export function setLanguage(lang: string): void {
  if (!supported().includes(lang)) return;
  currentLang = lang;
  try { localStorage.setItem(STORAGE_KEY, lang); } catch { /* private mode */ }
  updateSwitcherUI(lang);
  applyDocumentLanguage(lang);
  applyToRoot(document.body, lang);
  document.dispatchEvent(new CustomEvent('sk-language-changed', { detail: { lang } }));
}

export function getLanguage(): string {
  return currentLang || defaultLang();
}

export function t(key: string, fallbackText?: string): string {
  const v = getByPath(dictFor(getLanguage()), key);
  if (typeof v === 'string') return v;
  const fb = getByPath(dictFor(defaultLang()), key);
  if (typeof fb === 'string') return fb;
  return fallbackText ?? key;
}

/** Re-applies translations to a sub-tree (useful after dynamic DOM updates). */
export function apply(root: ParentNode = document.body): void {
  applyToRoot(root, getLanguage());
}

/** Initialises i18n on page load. Call after envConfig.brand is resolved (i.e. after loadConfig()). */
export function initI18n(): void {
  currentLang = detectLanguage();
  applyDocumentLanguage(currentLang);
  applyToRoot(document.body, currentLang);
  bindLangSwitchers();
}
